/** @jsx jsx */

import { ReactNode } from 'react';

import { ColorName, Surface, Text } from '@zapier/design-system';
import { css, jsx } from '@emotion/core';

import Dismiss from 'app/developer-v3/components/Dismiss';

const Styles = {
  root: padding => css`
    align-items: flex-start;
    display: flex;
    padding: ${padding};
  `,
  imageContainer: css`
    max-width: 80px;
    margin-right: 20px;
    width: 100%;
  `,
};

type Props = {
  /** Content of the help box */
  description: ReactNode;
  /** Border color */
  borderColor?: ColorName;
  /** Padding of the content box */
  padding?: string;
  /** Title */
  heading: ReactNode;
  /** SVG Image to be rendered */
  image?: ReactNode;
  /** Backgroiund color */
  backgroundColor?: ColorName;
  /** Callback of the dismissable click */
  onDismiss?: () => void;
};

/**
 * Renders a small amount of help or informational text for the user in a
 * highly visible block, usually at the top or bottom of a page.
 */
const HelpBox = ({
  description,
  heading,
  image,
  padding,
  borderColor,
  backgroundColor,
  onDismiss,
}: Props) => {
  return (
    <Surface backgroundColor={backgroundColor} borderColor={borderColor}>
      <div css={Styles.root(padding)}>
        {image && (
          <div aria-label="help image" css={Styles.imageContainer}>
            {image}
          </div>
        )}
        <div>
          <Text
            fontWeight={600}
            margin="0px 0px 10px 0px"
            tag="h4"
            type="ParagraphHeader3"
          >
            {heading}
          </Text>
          <Text tag="p">{description}</Text>
        </div>
        {onDismiss && <Dismiss onDismiss={onDismiss} />}
      </div>
    </Surface>
  );
};

HelpBox.defaultProps = {
  padding: '20px',
  borderColor: 'GrayWarm4',
  backgroundColor: 'transparent',
};

export default HelpBox;
